import orderBy from 'lodash.orderby'
import {makeAutoObservable, observable, runInAction} from 'mobx'
import ReactPerfectScrollbar from 'react-perfect-scrollbar'
import {
  FrameDebouncer,
  NativeDragScroll,
  prepareVirtualize,
  VirtualizeParams
} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {findRowPathById} from '@components/common/base/table/utils'
import {createUseStore} from '@containers/hooks'
import {Sort, TableColumn, TableDataType, TableProps, TableRow} from './types'

interface State<T extends TableDataType> {
  allRows: TableProps<T>['rows']
  rowsOnTop?: TableProps<T>['rows']
  order: (DataObjectId | string)[]
  columns: TableColumn<T>[]
  showHeaderShadow: boolean
  draggable: boolean
  isDraggableOut?: boolean
  displayedPages?: number
  elementsOnPage?: number
  showMoreAmount?: number
  draggingRowId?: DataObjectId
  sort?: Sort
  expanded: Map<string, DataObjectId | ''>
  hoverType: 'default' | 'managed'
  dragType: 'over' | 'between'
  virtualize?: VirtualizeParams
  perfectScrollbar: ReactPerfectScrollbar | undefined
  scrollableEl: HTMLElement | undefined
  containerEl: HTMLElement | undefined
  fromRow: number
  toRow: number
  highlightedRows?: string[]
  hoveredRowId: string | undefined
}

export class TableStore<T extends TableDataType> {
  virtualizeDebouncer = new FrameDebouncer()

  state: State<T> = {
    allRows: [],
    order: [],
    columns: [],
    draggable: false,
    showHeaderShadow: false,
    expanded: new Map(),
    hoverType: 'default',
    dragType: 'over',
    virtualize: undefined,
    perfectScrollbar: undefined,
    scrollableEl: undefined,
    containerEl: undefined,
    fromRow: 0,
    toRow: 0,
    hoveredRowId: undefined
  }

  // eslint-disable-next-line max-params
  constructor(
    defaultSort: Sort | undefined,
    rows: TableProps<T>['rows'],
    columns: TableProps<T>['columns'],
    elementsOnPage?: number,
    showMoreAmount?: number,
    dragType?: 'over' | 'between',
    virtualize?: VirtualizeParams,
    public onSortApply?: (sort: Sort | undefined) => void,
    public onRowHover?: (rowId: string | undefined) => void
  ) {
    makeAutoObservable(
      this,
      {virtualizeDebouncer: observable.ref},
      {autoBind: true}
    )

    if (elementsOnPage) {
      this.state.displayedPages = 1
      this.state.elementsOnPage = elementsOnPage
    }

    if (showMoreAmount) {
      this.state.showMoreAmount = showMoreAmount
    }

    if (defaultSort) {
      this.state.sort = defaultSort
      this.updateFromProps(rows, columns)
    }

    if (dragType) {
      this.state.dragType = dragType
    }

    this.state.virtualize = virtualize
  }

  get isDragTypeOver(): boolean {
    return this.state.dragType === 'over'
  }

  get isDragTypeBetween(): boolean {
    return this.state.dragType === 'between'
  }

  updateFromProps(
    rows: TableProps<T>['rows'],
    columns: TableProps<T>['columns'],
    rowsOnTop?: TableProps<T>['rows']
  ): void {
    this.state.order = rows.map(row => row.data.id)
    this.state.columns = columns

    this.state.allRows = rows
    this.state.rowsOnTop = rowsOnTop
    this.syncExpandedTasks()

    if (this.state.virtualize) {
      this.virtualize()
    }

    if (this.state.draggingRowId) {
      this.state.draggingRowId = undefined
    }
  }

  syncExpandedTasks(): void {
    if (!this.state.expanded.size) {
      return
    }

    const result: Map<string, DataObjectId | ''> = new Map()

    for (const [, expandedRowId] of this.state.expanded) {
      if (expandedRowId) {
        const newPath = findRowPathById(this.rows, expandedRowId)
        if (newPath) {
          result.set(newPath, expandedRowId)
        }
      }
    }

    this.state.expanded = result
  }

  setDraggable(draggable: boolean): void {
    this.state.draggable = draggable
  }

  setDraggableOutside(isDraggableOut: boolean): void {
    this.state.isDraggableOut = isDraggableOut
  }

  get currentRows(): Map<DataObjectId | string, TableRow<T>> {
    return new Map(this.state.allRows.map(row => [row.data.id, row]))
  }

  get rows(): TableRow<T>[] {
    let result: TableRow<T>[] = []
    const defaultSortedRows = this.state.order
      .map(itemId => {
        return this.currentRows.get(itemId)
      })
      .filter((item): item is TableRow<T> => !!item)

    if (!this.state.sort) {
      result = defaultSortedRows
    } else {
      const column = this.currentColumns[this.state.sort.columnIndex]
      const sortBy = column?.sortBy
      if (!sortBy) {
        result = defaultSortedRows
      } else {
        const column = this.currentColumns[this.state.sort.columnIndex]
        const emptyRows: TableRow<T>[] = []
        const rowsForSorting = column.missingValueRowsPlacement
          ? defaultSortedRows.filter(row => {
              if (sortBy(row.data) === undefined) {
                emptyRows.push(row)
                return false
              }
              return true
            })
          : defaultSortedRows

        result = orderBy(
          rowsForSorting,
          y => {
            const sortByValue = sortBy(y.data)
            return typeof sortByValue === 'string'
              ? sortByValue.toLowerCase()
              : sortByValue
          },
          this.state.sort.order ? 'desc' : 'asc'
        )

        if (column.missingValueRowsPlacement) {
          result =
            column.missingValueRowsPlacement === 'top'
              ? [...emptyRows, ...result]
              : [...result, ...emptyRows]
        }
      }
    }

    const {elementsOnPage, displayedPages, showMoreAmount, rowsOnTop} =
      this.state

    if (rowsOnTop?.length) {
      result = [...rowsOnTop, ...result]
    }

    if (displayedPages && elementsOnPage) {
      let amount = displayedPages * elementsOnPage

      if (showMoreAmount && displayedPages > 1) {
        amount = elementsOnPage + (displayedPages - 1) * showMoreAmount
      }

      return result.slice(0, amount)
    }

    return result
  }

  // get visibleAmountOfRows(): number {
  //   const {elementsOnPage, displayedPages, showMoreAmount, rowsOnTop} = this.state
  //
  //   if (!elementsOnPage || !displayedPages) {
  //     return
  //   }
  // }

  get currentColumns(): TableColumn<T>[] {
    return this.state.columns.filter(column => !column.hidden)
  }

  setColumnSort(columnIndex: number): void {
    const column = this.currentColumns[columnIndex]

    if (!column.sortBy) {
      return
    }
    const sort = this.state.sort
    const sameColumn = sort?.columnIndex === columnIndex

    this.state.sort =
      sameColumn && sort?.count === 1
        ? undefined
        : {
            columnIndex,
            order: sameColumn ? !sort?.order : false,
            count: sameColumn ? (sort?.count || 0) + 1 : 0
          }

    if (this.state.rowsOnTop?.length) {
      this.state.rowsOnTop = []
    }

    this.onSortApply?.(this.state.sort)
  }

  boardDragScroll(): void {
    const {scrollableEl} = this.state
    if (scrollableEl) {
      NativeDragScroll.start({scrollableEl, thresholdY: 50})
    }
  }

  setHeaderShadow(value: boolean): void {
    if (this.state.showHeaderShadow !== value) {
      this.state.showHeaderShadow = value
    }
  }

  setDraggingRowId(rowId?: DataObjectId): void {
    this.state.draggingRowId = rowId
  }

  setOrder(newOrder: (DataObjectId | string)[]): void {
    this.state.order = newOrder
  }

  showMore(): void {
    if (this.state.displayedPages) {
      this.state.displayedPages += 1
    }
  }

  toggleRow(rowIndex: string, rowId?: DataObjectId): void {
    if (this.state.expanded.has(rowIndex)) {
      this.state.expanded.delete(rowIndex)
    } else {
      this.state.expanded.set(rowIndex, rowId || '')
    }
  }

  setScrollableEl(el: HTMLElement): void {
    this.state.scrollableEl = el
  }

  setPerfectScrollbar(perfectScrollbar: ReactPerfectScrollbar): void {
    this.state.perfectScrollbar = perfectScrollbar
  }

  setContainerEl(el: HTMLElement): void {
    this.state.containerEl = el
  }

  scheduleVirtualize(): void {
    if (this.state.virtualize) {
      this.virtualizeDebouncer.runButNotOften(this.virtualize)
    }
  }

  setHighlightedRowId(rowIndex: string, value: boolean): void {
    if (value && !this.state.highlightedRows) {
      this.state.highlightedRows = []
    }

    const currentRowIdInState = this.state.highlightedRows?.includes(rowIndex)

    if (value && !currentRowIdInState) {
      this.state.highlightedRows?.push(rowIndex)
      return
    }

    if (!value && currentRowIdInState) {
      this.state.highlightedRows = this.state.highlightedRows?.filter(
        row => row !== rowIndex
      )
    }
  }

  private virtualize(): void {
    const {virtualize, scrollableEl, containerEl} = this.state
    if (virtualize && scrollableEl && containerEl) {
      const {scrollTop, clientHeight} = scrollableEl
      const {fromRow, toRow, height} = prepareVirtualize(
        scrollTop,
        clientHeight,
        this.state.allRows.length,
        virtualize
      )

      runInAction(() => {
        this.state.fromRow = fromRow
        this.state.toRow = toRow
      })
      containerEl.style.height = `${height}px`
      this.state.perfectScrollbar?.updateScroll()
    }
  }

  onRowHovered(rowId: string | undefined): void {
    if (this.state.hoveredRowId !== rowId) {
      this.state.hoveredRowId = rowId
      this.onRowHover?.(rowId)
    }
  }
}

const [TableContext, useTableStore] = createUseStore(TableStore)
export {TableContext, useTableStore}
